import { useCallback, useMemo, useRef, useState } from "react";
import useAdminLoad from "../../../hooks/useAdminLoad";
import * as crmApi from "../api/crmApi";

/** A locally-unique key for a step not yet round-tripped through the server. */
const localId = () => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch { /* insecure context */ }
  return `step-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const EMPTY_STEP = (bookAgainDefault) => ({
  id: localId(),
  offset: { value: 1, unit: "days", anchor: "after_trigger" },
  subject: "",
  body_html: "",
  book_again_button: bookAgainDefault,
});

/**
 * One sequence's editor state (`GET /api/automation/sequences/{id}`): the server copy
 * (`sequence`) and a locally-edited draft the screen renders and mutates, with a `dirty`
 * flag driving the unsaved-changes guard (design §F3: `beforeunload` + in-app link guard,
 * no data router `useBlocker` here).
 *
 * A SYSTEM sequence's trigger is fixed (409 `SYSTEM_TRIGGER_LOCKED` server-side); the
 * screen disables the TriggerSelect for one instead of racing the refusal.
 */
export default function useSequence(id) {
  const [sequence, setSequence] = useState(null);
  const [draft, setDraft] = useState(null);
  // The draft as last saved (or as loaded): compared against `draft` for `dirty`.
  const savedShape = useRef(null);

  const load = useCallback(async () => {
    const seq = await crmApi.getSequence(id);
    setSequence(seq);
    const shape = { name: seq.name, trigger: seq.trigger, steps: seq.steps || [] };
    setDraft(shape);
    savedShape.current = shape;
  }, [id]);
  const { loading, loadError, reload } = useAdminLoad(load);

  const dirty = useMemo(
    () => draft !== null && JSON.stringify(draft) !== JSON.stringify(savedShape.current),
    [draft],
  );

  const setName = useCallback((name) => setDraft((d) => ({ ...d, name })), []);
  const setTrigger = useCallback((trigger) => setDraft((d) => ({ ...d, trigger })), []);

  const addStep = useCallback((bookAgainDefault = false) => setDraft((d) => ({ ...d, steps: [...d.steps, EMPTY_STEP(bookAgainDefault)] })), []);
  const updateStep = useCallback((stepId, patch) => setDraft((d) => ({
    ...d,
    steps: d.steps.map((s) => (s.id === stepId ? { ...s, ...patch } : s)),
  })), []);
  const removeStep = useCallback((stepId) => setDraft((d) => ({
    ...d,
    steps: d.steps.filter((s) => s.id !== stepId),
  })), []);

  /** Strips the client-only local id from a step that never reached the server. */
  const wireSteps = (steps) => steps.map(({ id: stepId, ...rest }) => (
    // A real server id (present on every step this screen loaded) is kept; a step ADDED
    // in this session carries only the local uuid and is sent without one — the server
    // assigns its id on save.
    sequence?.steps?.some((s) => s.id === stepId) ? { id: stepId, ...rest } : { ...rest }
  ));

  const saveDraft = useCallback(async () => {
    const saved = await crmApi.updateSequence(id, { name: draft.name, trigger: draft.trigger, steps: wireSteps(draft.steps) });
    setSequence(saved);
    const shape = { name: saved.name, trigger: saved.trigger, steps: saved.steps || [] };
    setDraft(shape);
    savedShape.current = shape;
    return saved;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, draft, sequence]);

  const activate = useCallback(async () => {
    if (dirty) await saveDraft();
    const saved = await crmApi.activateSequence(id);
    setSequence(saved);
    return saved;
  }, [id, dirty, saveDraft]);

  /**
   * One step as an email to preview or test-send (`POST /api/automation/preview`): its
   * content as it stands on screen — unsaved edits and steps added in this session
   * included, nothing saved on the way. The server reads the sequence's stream and
   * system key from `sequence_id`; the step's own id plays no part.
   */
  const stepEmailDraft = useCallback((step) => ({
    kind: "sequence_step", sequence_id: id, subject: step.subject, body_html: step.body_html,
    offset: step.offset, book_again_button: !!step.book_again_button,
  }), [id]);

  return {
    sequence, draft, loading, loadError, reload, dirty,
    setName, setTrigger, addStep, updateStep, removeStep,
    saveDraft, activate, stepEmailDraft,
  };
}
